import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  hourMinutes,
  holdChoices,
  clockOn,
  shiftDay,
  visitorMap,
  type CheckoutRequest,
  type MapSpot,
} from '@kratispot/shared';
import { DayStrip } from '@/components/day-strip';
import { VenueMap } from '@/components/map/venue-map';
import { BookModal } from '@/components/visitor/book-modal';
import { BookedPanel } from '@/components/visitor/booked-panel';
import { Button } from '@/components/ui/button';
import { LanguageToggle } from '@/components/language-toggle';
import { Toasts, useToasts } from '@/components/toasts';
import { useAvailability, useAvailabilitySocket } from '@/hooks/use-availability';
import { ApiError, lookupBooking, startCheckout } from '@/lib/api';
import {
  forgetCheckout,
  forgetOwnBooking,
  recallCheckout,
  recallOwnBooking,
  rememberCheckout,
  rememberOwnBooking,
} from '@/lib/checkout-memory';
import { leaveFor } from '@/lib/leave-for';
import { pendingCheckout } from '@/lib/pending-checkout';
import { moneyOf } from '@/lib/format';
import { useTranslation } from '@/lib/i18n';

// The day a screen is on, as its address carries it. §8
export function onDay(date: string | null): Record<string, string> {
  return date === null ? {} : { date };
}

// A visitor never looks back, so the strip's own past days are never offered.
export function forwardDate(asked: string, today: string): string {
  return asked < today ? today : asked;
}

// The booking this tab holds, as the floor has to draw it. §12
export type OwnStay = { numbers: readonly number[]; startsOn: string; endsOn: string };

// Which tiles on the day shown belong to the tab's own booking, and no other's.
export function ownSpotIds(
  spots: readonly MapSpot[],
  own: OwnStay | null,
  date: string,
): ReadonlySet<string> {
  if (own === null || date < own.startsOn || date > own.endsOn) return new Set();
  return new Set(spots.filter((spot) => own.numbers.includes(spot.number)).map((spot) => spot.id));
}

// Everything the page needs to know about the clock: whether today can still
// be held at all, which is what §12 says the page must explain.
export function bookableToday(
  hold: { includedHour: number; latestHour: number; stepMinutes: 30 | 60 },
  nowMinutes: number,
): boolean {
  return holdChoices([], hold, nowMinutes).length > 0;
}

// The public booking page of one venue: availability, a form, a payment. §12
export function VisitorPage(): ReactElement {
  const { t, language } = useTranslation();
  const slug = useParams().slug as string;
  const [query, setQuery] = useSearchParams();
  // §8: the day is in the address, so a reload keeps the day they were on.
  const date = query.get('date');
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [booking, setBooking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [unpaid, setUnpaid] = useState<{ code: string; payUrl: string | null } | null>(null);
  const [done, setDone] = useState<{ code: string; email: string } | null>(null);
  const [own, setOwn] = useState<OwnStay | null>(null);
  const { state, reload } = useAvailability(slug, date);
  const { toasts, push } = useToasts();
  const answered = useRef(false);

  useAvailabilitySocket(
    slug,
    useCallback(() => reload(), [reload]),
  );

  // The tab's own booking, by the code and address it already holds. §12
  const claim = useCallback(
    (code: string, email: string) =>
      lookupBooking(slug, { code, email }).then((booking) => {
        rememberOwnBooking({ slug, code, email });
        setOwn({
          numbers: booking.spots.map((spot) => spot.number),
          startsOn: booking.startsOn,
          endsOn: booking.endsOn,
        });
      }),
    [slug],
  );

  // Back with a code; the address is the tab's, never the URL's. §12
  const returned = query.get('code');
  const day = query.get('date');
  useEffect(() => {
    if (returned === null || answered.current) return;
    answered.current = true;
    const email = recallCheckout(slug, returned);
    forgetCheckout();
    setQuery(onDay(day), { replace: true });
    if (email === null) return;
    claim(returned, email)
      .then(() => setDone({ code: returned, email }))
      .catch((error: unknown) => {
        // A payment that did not go through leaves a booking still waiting for
        // it, and its spots held. Saying "something went wrong" strands the
        // guest; the way back to the provider is what they need. §13
        const waiting = pendingCheckout(error);
        if (waiting === null) return push(t('visitorFailed'), 'bg-red-500');
        setUnpaid({ code: returned, payUrl: waiting.payUrl });
      });
  }, [returned, day, slug, setQuery, claim, push, t]);

  // A plain visit in the same tab still knows its booking, so the floor says so.
  useEffect(() => {
    if (returned !== null) return;
    const kept = recallOwnBooking(slug);
    if (kept === null) return;
    claim(kept.code, kept.email).catch(() => forgetOwnBooking());
  }, [returned, slug, claim]);

  const pick = useCallback((spot: MapSpot) => {
    setPicked((current) => {
      const next = new Set(current);
      if (!next.delete(spot.id)) next.add(spot.id);
      return next;
    });
  }, []);

  // On the way to a provider's own page. A fetch the browser kills mid-navigation
  // rejects like any other failure, and the page must not blame itself for it:
  // the visitor saw "something went wrong" and then a working checkout. §13
  if (leaving)
    return (
      <p role="status" className="p-8 text-center text-sm">
        {t('leavingForPayment')}
      </p>
    );

  // §13: a refused payment must not read as a lost booking. The spots are still
  // held for the TTL, so the honest thing is to say so and offer the way back.
  if (unpaid !== null)
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <b className="text-[17px] font-extrabold">{t('payFailedTitle')}</b>
        <p className="max-w-xs text-[13px] text-muted-foreground">
          {t(unpaid.payUrl === null ? 'payFailedNoWay' : 'payFailedNote')}
        </p>
        <div className="flex w-full max-w-xs flex-col gap-2">
          {unpaid.payUrl !== null && (
            <Button onClick={() => leaveFor(unpaid.payUrl as string)}>{t('resumePayment')}</Button>
          )}
          <Button variant="outline" onClick={() => setUnpaid(null)}>
            {t('back')}
          </Button>
        </div>
      </div>
    );

  if (state.phase !== 'ready')
    return (
      <p role={state.phase === 'loading' ? undefined : 'alert'} className="p-8 text-center text-sm">
        {t(
          state.phase === 'loading'
            ? 'loading'
            : state.phase === 'unknown'
              ? 'venueUnknown'
              : 'errorGeneric',
        )}
      </p>
    );

  const answer = state.availability;
  const map = visitorMap(answer);
  const spots = map.spots.filter((spot) => picked.has(spot.id));
  const perDay = spots.reduce(
    (sum, spot) =>
      sum + (answer.venue.zones.find((zone) => zone.key === spot.zoneKey)?.dayPriceCents ?? 0),
    0,
  );
  // Past the venue's last hold hour there is no deadline to offer, so today is
  // not bookable and the page says which hour went by. §12
  const closed = map.date === map.today && !bookableToday(answer.venue.hold, answer.nowMinutes);

  async function book(request: CheckoutRequest): Promise<void> {
    setBusy(true);
    try {
      const started = await startCheckout(slug, request);
      if (started.redirectUrl !== null) {
        rememberCheckout({
          slug,
          code: started.code,
          email: request.guestEmail,
          date: request.startsOn,
        });
        setLeaving(true);
        leaveFor(started.redirectUrl);
        return;
      }
      setBooking(false);
      setPicked(new Set());
      setDone({ code: started.code, email: request.guestEmail });
      await claim(started.code, request.guestEmail).catch(() => undefined);
    } catch (error) {
      const taken = error instanceof ApiError && error.status === 409;
      push(t(taken ? 'visitorTaken' : 'visitorFailed'), 'bg-red-500');
      reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full flex-col bg-background">
      <header className="flex shrink-0 items-center justify-between gap-2 border-b px-3 px-safe py-2.5 pt-safe">
        <b className="truncate text-[15px] font-extrabold">{answer.venue.name}</b>
        <span className="flex shrink-0 items-center gap-2">
          <Link
            to={`/${slug}/booking`}
            className="rounded-full border px-3 py-1.5 text-xs font-bold text-primary"
          >
            {t('manageBooking')}
          </Link>
          <LanguageToggle />
        </span>
      </header>

      <p className="px-4 pt-3 text-center text-[15px] font-extrabold">
        {t('visitorLead')}
        <small className="mt-0.5 block text-[11.5px] font-semibold text-muted-foreground">
          {t('selectHint')}
        </small>
      </p>

      <DayStrip
        date={map.date}
        today={map.today}
        mode={map.mode}
        earliest={map.today}
        onPick={(asked) => {
          setPicked(new Set());
          setQuery(onDay(forwardDate(asked, map.today)));
        }}
      />

      {closed && (
        <p
          role="status"
          className="mx-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-center text-[12.5px] font-semibold text-amber-900"
        >
          {t('visitorClosed')}{' '}
          <span className="font-normal">
            ({hourMinutes(answer.venue.hold.latestHour) / 60}:00) · {t('visitorClosedHint')}
          </span>
        </p>
      )}

      <div className="px-2 pt-2 pb-24 sm:px-3">
        <VenueMap
          map={map}
          selected={picked}
          pulsing={EMPTY}
          painting={false}
          visitor
          mine={ownSpotIds(map.spots, own, map.date)}
          onPick={closed ? NO_PICK : pick}
          onPointerDown={NO_POINTER}
        />
      </div>

      {picked.size > 0 && !closed && (
        <div
          role="toolbar"
          aria-label={t('bookNow')}
          className="fixed bottom-3 left-1/2 z-64 mb-safe flex max-w-[96vw] -translate-x-1/2 items-center gap-2 rounded-2xl bg-foreground px-3 py-2 text-[13px] text-background shadow-2xl"
        >
          <b className="px-1 whitespace-nowrap" data-testid="visitor-bar">
            {picked.size} · {moneyOf(perDay, answer.venue.currency, language)}
            {t('perDay')}
          </b>
          <button
            type="button"
            onClick={() => setBooking(true)}
            className="rounded-xl bg-primary px-3.5 py-2 font-bold whitespace-nowrap text-primary-foreground"
          >
            {t('bookNow')}
          </button>
          <button
            type="button"
            aria-label={t('clearSelection')}
            onClick={() => setPicked(new Set())}
            className="rounded-xl px-2 py-1.5 font-bold opacity-70"
          >
            ✕
          </button>
        </div>
      )}

      {booking && (
        <BookModal
          venue={map.venue}
          spots={spots}
          date={map.date}
          today={map.today}
          nowMinutes={answer.nowMinutes}
          paymentMode={answer.venue.paymentMode}
          cancellation={answer.venue.cancellation}
          busy={busy}
          onClose={() => setBooking(false)}
          onSubmit={(request) => void book(request)}
        />
      )}

      {done !== null && (
        <BookedPanel
          slug={slug}
          code={done.code}
          email={done.email}
          onClose={() => setDone(null)}
        />
      )}
      <Toasts toasts={toasts} undoLabel={t('undo')} />
    </div>
  );
}

const EMPTY: ReadonlySet<string> = new Set();

const NO_POINTER = (): void => undefined;

const NO_PICK = (): void => undefined;

export { clockOn, shiftDay };
